// react
import { useForm } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

// api
import { type ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';
import type { GetAlarmConfigurationDto } from '@/api/generated/recalc/endpoints/v2/alarmConfiguration';

// shared components
import { SubmitButton } from '@/components/ui/button';

// feature-hooks
import { useUpdateAlarmConfiguration } from '@/features/alarm-hub/hooks/useUpdateAlarmConfiguration';

// relative
import { normalizeDataStreamOptions } from '../dataStreamUtils';
import {
  buildConfigurationZodSchema,
  inferSchemaFromObject,
  isObject,
} from '../schemaUtils';
import { AlarmConfigurationDataStreamFields } from '../shared/AlarmConfigurationDataStreamFields';
import { AlarmConfigurationDetailsFields } from '../shared/AlarmConfigurationDetailsFields';
import {
  toAlarmConfigurationAssociations,
  toAlarmConfigurationFormValues,
  toInitialSelectedDataStreamKeys,
  toPutAlarmConfigurationDto,
} from '../shared/alarmConfigurationForm.mappers';
import { createAlarmConfigurationFormSchema } from '../shared/alarmConfigurationForm.schema';
import { RHFJsonSchemaFields } from '../shared/RHFJsonSchemaFields';
import type { JsonSchemaNode, StandaloneFormProps } from '../types';

// react-hook-form
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const DEFAULT_UOT_SUBTYPE = 'ThresholdExceeded';

type EditUsageOverTimeConfigurationProps = StandaloneFormProps & {
  alarmConfigurationId: number;
  alarmConfiguration: GetAlarmConfigurationDto;
  units: ReadUnitDto[];
};

export function EditUsageOverTimeConfiguration({
  configurationSchema,
  alarmConfigurationId,
  alarmConfiguration,
  units,
}: EditUsageOverTimeConfigurationProps) {
  const { t } = useTranslation(['forms', 'validation']);

  const { updateAlarmConfiguration, isUpdating } =
    useUpdateAlarmConfiguration();

  let parsedSchema: JsonSchemaNode | null = null;
  try {
    parsedSchema = configurationSchema
      ? (JSON.parse(configurationSchema) as JsonSchemaNode)
      : null;
  } catch {
    parsedSchema = null;
  }

  const resolvedSchema: JsonSchemaNode | null = parsedSchema
    ? parsedSchema
    : isObject(alarmConfiguration.configurations)
      ? inferSchemaFromObject(alarmConfiguration.configurations)
      : null;

  const typedUnits = units as ReadUnitDto[];
  const dataStreamOptions = normalizeDataStreamOptions(typedUnits);

  const initialSelectedDataStreamKeys = toInitialSelectedDataStreamKeys(
    alarmConfiguration.associations?.dataStreams ?? [],
    dataStreamOptions,
  );

  let configurationZodSchema: z.ZodType<
    Record<string, unknown>,
    Record<string, unknown>
  >;
  let isConfigurationSchemaInvalid = false;
  try {
    configurationZodSchema = buildConfigurationZodSchema(resolvedSchema);
  } catch {
    configurationZodSchema = z.record(z.string(), z.unknown());
    isConfigurationSchemaInvalid = true;
  }

  const schema = createAlarmConfigurationFormSchema(t, configurationZodSchema);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toAlarmConfigurationFormValues(
      alarmConfiguration,
      resolvedSchema,
      DEFAULT_UOT_SUBTYPE,
      initialSelectedDataStreamKeys,
    ),
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values) => {
    const associations = toAlarmConfigurationAssociations(
      typedUnits,
      dataStreamOptions,
      values.dataStreamKeys,
    );

    const updatePayload = toPutAlarmConfigurationDto(
      values,
      'UsageOverTime',
      associations,
    );

    await updateAlarmConfiguration(alarmConfigurationId, updatePayload);
  });

  if (!resolvedSchema) {
    return (
      <Alert severity="warning">
        {t('alarmConfigurationForm.schemaNotSupported')}
      </Alert>
    );
  }

  if (isConfigurationSchemaInvalid) {
    return (
      <Alert severity="error">
        {t('alarmConfigurationForm.schemaInvalid')}
      </Alert>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">
                {t('alarmConfigurationForm.detailsTitle', {
                  ns: 'forms',
                  defaultValue: 'Details',
                })}
              </Typography>

              <AlarmConfigurationDetailsFields control={control} />

              <AlarmConfigurationDataStreamFields
                control={control}
                dataStreamOptions={dataStreamOptions}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">
                {t('alarmConfigurationForm.settingsTitle', {
                  ns: 'forms',
                  defaultValue: 'Configuration Settings',
                })}
              </Typography>

              <RHFJsonSchemaFields
                schema={resolvedSchema}
                control={control}
                alarmType="UsageOverTime"
              />
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end">
          <SubmitButton disabled={isUpdating || !isValid}>
            {isUpdating && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {t('common.save')}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
